import type { SchoolTestSchoolsParams } from '@mathflat/domain/SchoolTest'
import type { WorkbookSecuringType } from '@mathflat/domain/Workbook'
import type { History } from 'history'
import { uniqBy } from 'lodash-es'
import { makeAutoObservable, runInAction } from 'mobx'

import { schoolTestController } from '@/controller/schoolTest.controller'
import { SidoTreeNode, SigunguTreeNode } from '@/modules/region/interfaces/RegionTreeNode.interface'
import {
  aggregateSecuringType,
  containsSecondaryType,
} from '@/modules/schooTest2/util/securingTypeUtils'
import type { ByPaperLocationState } from '@/page/MakeWorksheet/MakeWorksheetBy/ByPaper/ByPaper'
import { RouteName } from '@/router/RouteName'
import modalService from '@/service/modal.service'
import { stringUtils } from '@/utils/String'
import { mixpanelClient } from '@/utils/trackings/mixpanel'

import { SchoolTreeNode } from '../models/SchoolTreeNode.model'
import { SchoolTestSearchStore, type SearchStatus } from './SchoolTestSearch.store'

export type TreeNode = {
  id: string
  name: string
  type?: string
  children?: TreeNode[]
  maxCheckedLength?: number
  isAssistPaperScanner?: boolean
  securingTypes?: WorkbookSecuringType[]
}

export class SchoolsUnderRegionStore {
  constructor({ enablePaperScannerAssist = false }: { enablePaperScannerAssist?: boolean } = {}) {
    makeAutoObservable(this)
    this._enablePaperScannerAssist = enablePaperScannerAssist
  }
  private _enablePaperScannerAssist: boolean
  private _checkedSchools = new Map<
    SchoolTreeNode['id'],
    {
      id: SchoolTreeNode['id']
      name: SchoolTreeNode['name']
      securingTypes: SchoolTreeNode['securingTypes']
    }
  >()

  private _sidoList: SidoTreeNode[] = []
  private _sigunguList: SigunguTreeNode[] = []
  private _schoolList: SchoolTreeNode[] = []

  private _currentSelectedSidoId: string | null = null
  private _currentSelectedSiGunGuId: string | null = null
  private _currentSelectedSchoolId: string | null = null

  // 학교 목록 API 로딩 상태
  private _isLoadingSchools = false
  // 학교급/지역 변경 시 진행 중인 이전 요청을 취소하기 위한 컨트롤러
  private _loadSchoolsAbortController: AbortController | null = null

  get isLoadingSchools() {
    return this._isLoadingSchools
  }

  resetAllRegionSchoolFilters() {
    this._checkedSchools.clear()
    this._currentSelectedSidoId = null
    this._currentSelectedSiGunGuId = null
    this.resetSearchSchoolList()
    this._schoolSearchStore.updateSearchStatus('initial')
  }

  get isDirty() {
    return (
      this._checkedSchools.size > 0 ||
      this._currentSelectedSidoId !== null ||
      this._currentSelectedSiGunGuId !== null ||
      this._currentSelectedSchoolId !== null ||
      this._schoolSearchStore.searchStatus !== 'initial'
    )
  }

  get checkedSchools() {
    return this._checkedSchools
  }

  setCheckedSchools(schools: typeof this._checkedSchools) {
    this._checkedSchools = new Map(schools)
  }

  get checkedSchoolIds() {
    return [...this._checkedSchools.keys()]
      .filter((id) => id.includes('S'))
      .map((schoolId) => schoolId.split('_')[1])
  }

  get checkedSchoolsAggregatedSecuringType() {
    if (this._checkedSchools.size === 0) return undefined

    const checkedSchools = [...this._checkedSchools.values()]
    return aggregateSecuringType(checkedSchools.flatMap((school) => school.securingTypes))
  }

  get isContainSecondaryTypeSchoolTest() {
    return containsSecondaryType(this.checkedSchoolsAggregatedSecuringType)
  }

  get currentSelectedSidoId() {
    return this._currentSelectedSidoId ?? '시/구/군'
  }

  setSelectedSidoId(sidoId: string) {
    this._currentSelectedSidoId = sidoId
  }

  get currentSelectedSigunguId() {
    return this._currentSelectedSiGunGuId ?? '학교'
  }

  setSelectedSigunguId(sigunguId: string) {
    this._currentSelectedSiGunGuId = sigunguId
  }

  get currentSelectedSchoolId() {
    return this._currentSelectedSchoolId
  }

  setSelectedSchoolId(schoolId: string) {
    this._currentSelectedSchoolId = schoolId
  }

  getSidoTree() {
    return this.sidoTree
  }

  get sidoList() {
    return this._sidoList
  }

  setSidoList(sidoList: SidoTreeNode[]) {
    this._sidoList = sidoList
  }

  getSigunguTree() {
    return this.sigunguTree
  }

  get sigunguList() {
    return this._sigunguList
  }

  setSigunguList(sigunguList: SigunguTreeNode[]) {
    this._sigunguList = sigunguList
  }

  getSchoolTree() {
    return this.schoolTree
  }

  get schoolList() {
    return this._schoolList
  }

  setSchoolList(schoolList: SchoolTreeNode[]) {
    this._schoolList = schoolList
  }
  toggleCheckedSchool = (
    checked: boolean,
    { id, name, securingTypes }: Parameters<typeof this._checkedSchools.set>[1],
  ) => {
    if (checked) {
      this._checkedSchools.set(id, {
        id,
        name,
        securingTypes,
      })
    } else {
      this._checkedSchools.delete(id)
    }
  }
  onAllCheckSchoolList(id: string, checked: boolean) {
    this._schoolList.forEach((school) => {
      if (this._enablePaperScannerAssist && school.securingTypes.length === 0) return
      if (school.id.startsWith(id)) {
        this.toggleCheckedSchool(checked, {
          id: school.id,
          name: school.name,
          securingTypes: school.securingTypes,
        })
      }
    })
  }

  get sidoTree(): TreeNode {
    return {
      id: 'SIDO',
      name: '지역',
      children: this._sidoList.map((sido) => ({
        id: sido.id,
        name: sido.name,
        children: this._sigunguList
          .filter((sigugun) => sigugun.sido === sido.id)
          .map((sigungu) => ({
            id: sigungu.id,
            name: sigungu.name,
            children: this._schoolList
              .filter((school) => {
                return school.sigungu === sigungu.id
              })
              .map((school) => ({
                id: school.id,
                name: school.name,
              })),
          })),
        maxCheckedLength: this._schoolList.filter((school) => school.sido === sido.id).length,
      })),
    }
  }

  get sigunguTree(): TreeNode {
    return {
      id: this.currentSelectedSidoId,
      name: '시/구/군',
      children: this._sigunguList
        .filter((sigungu) => sigungu.sido === this.currentSelectedSidoId)
        .map((sigungu) => ({
          id: sigungu.id,
          name: sigungu.name,
          children: this._schoolList.map((school) => ({
            id: school.id,
            name: school.name,
          })),
          maxCheckedLength: this._schoolList.filter((school) => {
            if (this._enablePaperScannerAssist) {
              return school.sigungu === sigungu.id && !school.isAssistPaperScanner
            }
            return school.sigungu === sigungu.id
          }).length,
        })),
    }
  }

  get schoolTree(): TreeNode {
    return {
      id: this.currentSelectedSigunguId,
      name: '학교',
      children: this._schoolList
        .filter((school) => school.sigungu === this.currentSelectedSigunguId)
        .map((school) => ({
          id: school.id,
          name: school.name,
          type: school.type,
          isAssistPaperScanner: this._enablePaperScannerAssist && school.isAssistPaperScanner,
          securingTypes: school.securingTypes,
        })),
    }
  }

  async loadSchools(params: SchoolTestSchoolsParams) {
    // 진행 중인 이전 요청이 있으면 취소
    this._loadSchoolsAbortController?.abort()
    const abortController = new AbortController()
    this._loadSchoolsAbortController = abortController

    this._isLoadingSchools = true

    try {
      const schoolsUnderRegion = await schoolTestController.fetchSchoolsBySchoolInfo(
        params,
        abortController.signal,
      )

      runInAction(() => {
        this._sidoList = schoolsUnderRegion.map(
          (sido) =>
            new SidoTreeNode({
              type: 'SIDO',
              code: sido.code,
              sidoName: sido.sidoName,
              sigunguName: null,
            }),
        )

        this._sigunguList = schoolsUnderRegion.flatMap((sido) =>
          sido.child.map(
            (sigungu) =>
              new SigunguTreeNode({
                code: sigungu.code,
                sidoName: sigungu.parentCode,
                sigunguName: sigungu.sigunguName,
                type: 'SIGUNGU',
              }),
          ),
        )

        this._schoolList = [...schoolsUnderRegion].flatMap((sido) =>
          [...sido.child].flatMap((sigungu) =>
            sigungu.child.map(
              (school) =>
                new SchoolTreeNode({
                  code: school.code,
                  name: school.name,
                  type: school.type,
                  provideType: school.provideType,
                  regionCode: school.regionCode,
                  status: school.status,
                  priority: school.priority,
                  sidoName: sido.code,
                  sigunguName: sigungu.code,
                  sidoCode: sido.sidoName,
                  sigunguCode: sigungu.sigunguName,
                  securingTypes: school.securingTypes,
                  provideTypes: school.provideTypes,
                }),
            ),
          ),
        )
      })
    } catch (error) {
      // 취소된 요청은 무시
      if (abortController.signal.aborted) return
      throw error
    } finally {
      if (!abortController.signal.aborted) {
        runInAction(() => {
          this._isLoadingSchools = false
        })
      }
    }
  }

  // SchoolTestSearchStore
  private _schoolSearchStore = new SchoolTestSearchStore() // 학교명 검색 스토어

  get searchedSchools() {
    return [...this._schoolSearchStore.searchedSchoolList]
  }

  get searchStatus() {
    return this._schoolSearchStore.searchStatus
  }
  get searchText() {
    return this._schoolSearchStore.searchText
  }

  updateSearchStatus(status: SearchStatus) {
    this._schoolSearchStore.updateSearchStatus(status)
  }

  setSearchText(search: string) {
    this._schoolSearchStore.setSearchText(search)
  }

  resetSearchSchoolList() {
    this._schoolSearchStore.reset()
  }

  clearSearch() {
    this._schoolSearchStore.setSearchText('')
    this.searchSchoolName('')
  }

  async searchSchoolName(search: string) {
    if (search.trim() === '') {
      this._schoolSearchStore.updateSearchStatus('cleared')
      return
    }

    try {
      const filteredSchoolsBySearch = this._schoolList.filter((school) =>
        school.name.includes(search),
      )

      this._schoolSearchStore.setSearchedSchoolList(
        filteredSchoolsBySearch.sort((a, b) => {
          // 기출스캐너로 유도 가능한 학교가 있으면 가장 뒤로 정렬
          if (a.isAssistPaperScanner) return 1
          if (b.isAssistPaperScanner) return -1
          return 0
        }),
      )
      this._schoolSearchStore.updateSearchStatus('searched')

      mixpanelClient.track('T35-13_학교별기출학습지_학교명검색', {
        Sch_Search_Keyword: search,
        Sch_Search_Exist: filteredSchoolsBySearch.length > 0,
      })
    } catch (error) {
      console.error(error)
    }
  }

  // for data tracking
  get selectedRegionsInKor() {
    const selectedSchools = this.schoolList.filter((school) =>
      this.checkedSchoolIds.includes(school.schoolCode),
    )

    const regionPropertyValue = selectedSchools.map((school) =>
      stringUtils.mergePropertiesWithChar(school.sidoCode, school.sigunguCode),
    )

    return uniqBy(regionPropertyValue, (region) => region)
  }

  get selectedSchoolsWithRegionInKor() {
    const selectedSchools = this.schoolList.filter((school) =>
      this.checkedSchoolIds.includes(school.schoolCode),
    )

    return selectedSchools.map((school) =>
      stringUtils.mergePropertiesWithChar(
        stringUtils.mergePropertiesWithChar(school.sidoCode, school.sigunguCode),
        school.name,
      ),
    )
  }

  goToByPaper = (
    {
      schoolId,
      schoolType,
      trieKey,
      schoolTestType,
      schoolTestYear,
      schoolName,
    }: ByPaperLocationState,
    { push }: { push: History['push'] },
  ) => {
    if (!this._enablePaperScannerAssist) return
    modalService.openConfirmModal(
      {
        content: (
          <div>
            &apos;기출 업로드&apos;에서 원본을 업로드하시면, <br />
            바로 유사 학습지를 생성할 수 있습니다. 계속하시겠습니까?
            <br />
            (화면을 이동하면, 선택했던 내용들이 저장되지 않습니다.)
          </div>
        ),
        onCancel: (e) => {
          e.stopPropagation()
          modalService.closeModal()
        },
        actionButtonText: '이동',
        onClick: () => {
          modalService.closeModal()
          if (!schoolId) return
          const _schoolId = schoolId.split('_')[1]

          push({
            pathname: RouteName.makeWorksheetByPaper,
            state: {
              schoolType,
              trieKey,
              schoolTestType,
              schoolTestYear,
              schoolId: _schoolId,
              schoolName,
            },
          })
        },
      },
      {
        hasCloseButton: false,
        onClose: (e) => {
          e?.stopPropagation()
          return true
        },
      },
    )
  }
}
